import { useEffect, useState } from "react";
import { MapPin, Clock, Mail, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { api } from "@/lib/api";
import { BUSINESS } from "@/data/content";

export default function Contact() {
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    return (
        <main className="pt-32 sm:pt-40 pb-24">
            <Seo title="Contact Us" path="/contact" description="Contact Innovative Fusion Hives — 4401 Atlantic Ave., Suite 200, Long Beach, CA 90807. Open Monday to Friday, 9:00 AM to 5:00 PM. Start your project inquiry." />
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="contact-hero">
                <Reveal>
                    <Eyebrow>Contact Us</Eyebrow>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                        Let us build something worth launching.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
                        Send a project inquiry below and we will respond during business hours with clear next steps.
                    </p>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-14 grid gap-8 lg:grid-cols-5" data-testid="contact-content">
                <Reveal className="lg:col-span-3">
                    <div className="rounded-2xl border border-white/10 bg-hive-card p-7 sm:p-9">
                        <h2 className="font-display text-xl font-bold text-white mb-6">Project Inquiry</h2>
                        <LeadForm />
                    </div>
                </Reveal>
                <div className="lg:col-span-2 space-y-6">
                    <Reveal delay={0.08}>
                        <div className="rounded-2xl border border-white/10 bg-hive-card p-7" data-testid="contact-info-card">
                            <h2 className="font-display text-lg font-bold text-white">Innovative Fusion Hives</h2>
                            <ul className="mt-5 space-y-4 text-sm text-slate-300">
                                <li className="flex gap-3">
                                    <MapPin className="h-5 w-5 shrink-0 text-violet-400" />
                                    <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-address-link" className="hover:text-white transition-colors">
                                        {BUSINESS.address1}<br />{BUSINESS.address2}
                                    </a>
                                </li>
                                <li className="flex gap-3">
                                    <Clock className="h-5 w-5 shrink-0 text-violet-400" />
                                    <span data-testid="contact-business-hours">Office hours: {BUSINESS.hours}</span>
                                </li>
                                <li className="flex gap-3">
                                    <Mail className="h-5 w-5 shrink-0 text-violet-400" />
                                    {settings?.contact_email
                                        ? <a href={`mailto:${settings.contact_email}`} data-testid="contact-email-link" className="hover:text-white transition-colors">{settings.contact_email}</a>
                                        : <span className="text-slate-500 italic">Direct email will be published soon — the inquiry form reaches us today.</span>}
                                </li>
                                <li className="flex gap-3">
                                    <Phone className="h-5 w-5 shrink-0 text-violet-400" />
                                    {settings?.phone
                                        ? <a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} data-testid="contact-phone-link" className="hover:text-white transition-colors">{settings.phone}</a>
                                        : <span className="text-slate-500 italic">Phone line coming soon.</span>}
                                </li>
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.14}>
                        <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="contact-map">
                            <iframe
                                title="Innovative Fusion Hives office location — 4401 Atlantic Ave., Suite 200, Long Beach, CA 90807"
                                src={BUSINESS.mapsEmbed}
                                className="h-72 w-full grayscale-[35%] contrast-[1.05]"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
